import React, { useMemo } from 'react'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { rem } from 'polished'
import styled from 'styled-components'
import useTourV2VariationDetails from 'hooks/TourV2/useTourV2VariationDetails'
import { getInclusionIcon } from 'lib/TourV2/getTourV2Icons'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import { TOUR_V2_PACKAGE_OPTION_BASE, TOUR_V2_PACKAGE_OPTION_EXTENDED } from 'constants/tours'
import Modal from 'components/Luxkit/Modal/Modal'

interface Props {
  variation: Tours.V2OfferVariation
  offer: Tours.V2Offer
}
const CaptionContainer = styled.div`
  align-items: center;
  display: flex;

  > * + *::before {
    content: '·';
    margin: 0 ${rem(4)};
    speak-as: bullets;
    speak: none;
  }

  &.horizontal-view {
     margin-top: ${rem(8)};
  }
`
const InclusionContainer = styled.div`
  margin-bottom: ${rem(24)}
`
const ListInclusion = styled.div`
  margin-top: ${rem(12)};
`
const ListItem = styled.div`
padding: ${rem(4)} 0;
`

function TourV2AllInclusionsModal({ variation, offer }: Props) {
  const offerVariations = useMemo(() => {
    return Object.values(offer.variations)
  }, [offer])
  const baseVariation = useMemo(() => {
    return offerVariations.find((variation) => variation.packageOptionName === TOUR_V2_PACKAGE_OPTION_BASE)
  }, [offerVariations])
  const extendedVariation = useMemo(() => {
    return offerVariations.find((variation) => variation.packageOptionName === TOUR_V2_PACKAGE_OPTION_EXTENDED)
  }, [offerVariations])
  const {
    durationInDaysCount,
    countriesCount,
    placesCount,
    hasTheSameStartAndEndLocation,
    startLocation,
    endLocation,
  } = useTourV2VariationDetails(variation)

  return <Modal title={variation.name}>
    <CaptionContainer>
      <BodyText variant="medium" colour="neutral-two">
        {hasTheSameStartAndEndLocation && <span>Starts and ends in <b>{endLocation}</b></span>}
        {!hasTheSameStartAndEndLocation && <span>Starts in <b>{startLocation}</b>. Ends in <b>{endLocation}</b></span>}
      </BodyText>
      <BodyText variant="medium">{durationInDaysCount}</BodyText>
      {placesCount && <BodyText variant="medium">{placesCount}</BodyText>}
      {countriesCount && <BodyText variant="medium">{countriesCount}</BodyText>}
    </CaptionContainer>
    <InclusionContainer>
      <ListInclusion>
        <BodyText weight="bold" variant="large">Highlights included in Base</BodyText>
        {!!baseVariation?.inclusionItems?.length && baseVariation.inclusionItems.map(inclusion => {
          const InclusionIcon = getInclusionIcon(inclusion.categoryIcon.split(',')[0])
          return (
            <ListItem key={inclusion.inclusionBody}>
              <BodyTextBlock key={inclusion.inclusionBody} weight="normal" variant="medium" startIcon={<InclusionIcon/>}>
                {inclusion.inclusionBody}
              </BodyTextBlock>
            </ListItem>

          )
        })}
      </ListInclusion>
      <ListInclusion>
        <BodyText weight="bold" variant="large">Additional extension highlights</BodyText>
        {!!extendedVariation?.inclusionItems?.length && extendedVariation.inclusionItems.map(inclusion => {
          const InclusionIcon = getInclusionIcon(inclusion.categoryIcon.split(',')[0])
          return (
            <ListItem key={inclusion.inclusionBody}>
              <BodyTextBlock key={inclusion.inclusionBody} weight="normal" variant="medium" startIcon={<InclusionIcon/>}>
                {inclusion.inclusionBody}
              </BodyTextBlock>
            </ListItem>
          )
        })}
      </ListInclusion>

    </InclusionContainer>
  </Modal>
}

export default TourV2AllInclusionsModal
